import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, LogOut } from "lucide-react";

/**
 * MobileDrawer — slide-in replica of the full desktop sidebar for mobile
 * users. Replaces the 4-item bottom nav as the primary navigation on phones
 * so patients can reach every page (Medical Records, Statistics, AI Report,
 * Reports, etc.) that was only on the desktop sidebar.
 */
export function MobileDrawer({ open, onClose, items, basePath, user, onSignOut }) {
    const navigate = useNavigate();
    const { pathname, hash } = useLocation();
    const current = (hash || "#overview").replace("#", "");

    // Lock body scroll while the drawer is open
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Close on escape
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const go = (id) => {
        navigate(`${basePath}#${id}`);
        onClose?.();
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`md:hidden fixed inset-0 bg-black/50 z-40 transition-opacity ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
                onClick={onClose}
                data-testid="mobile-drawer-backdrop"
            />
            {/* Drawer */}
            <aside
                className={`md:hidden fixed top-0 bottom-0 left-0 w-[82%] max-w-[320px] bg-white z-50 shadow-2xl transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "-translate-x-full"}`}
                data-testid="mobile-drawer"
                role="dialog"
                aria-hidden={!open}
            >
                {/* Header */}
                <div className="p-5 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <img src="/medx-logo.svg" alt="Medx AI" className="h-6 w-auto" onError={(e) => { e.target.style.display = "none"; }} />
                        <div>
                            <div className="font-display text-base font-bold leading-tight">medx<span className="text-[var(--medx-red)]">.ai</span></div>
                            <div className="text-[9px] font-mono tracking-[0.22em] text-gray-400">PATIENT APP</div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-gray-100 grid place-items-center text-gray-500"
                        aria-label="Close"
                        data-testid="mobile-drawer-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* User card */}
                {user && (
                    <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white text-xs font-bold overflow-hidden">
                            {user.photo_url ? <img src={user.photo_url} alt="" className="w-full h-full object-cover" /> : (user.first_name || user.name || "U").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                            <div className="font-semibold text-sm truncate">{user.first_name || user.name}</div>
                            <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
                        </div>
                    </div>
                )}

                {/* Nav items */}
                <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1" data-testid="mobile-drawer-nav">
                    {items.map((it) => {
                        const Icon = it.icon;
                        const isActive = (basePath === pathname) && (current === it.id);
                        const isDisabled = !!it.disabled;
                        return (
                            <button
                                key={it.id}
                                onClick={() => {
                                    if (isDisabled) {
                                        it.onDisabledClick?.();
                                        onClose?.();
                                        return;
                                    }
                                    go(it.id);
                                }}
                                className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium text-left transition-colors ${
                                    isDisabled ? "text-gray-400 cursor-not-allowed opacity-60" :
                                    isActive ? "bg-gradient-to-r from-[var(--medx-red)]/10 to-[var(--medx-orange)]/10 text-[var(--medx-red)]" :
                                    "text-gray-700 hover:bg-gray-50"
                                }`}
                                data-testid={`mobile-drawer-${it.id}`}
                            >
                                <Icon className={`w-4 h-4 ${isActive && !isDisabled ? "text-[var(--medx-orange)]" : ""}`} />
                                <span className="flex-1 truncate">{it.label}</span>
                                {isDisabled ? (
                                    <span className="text-[9px] font-mono tracking-wider px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">OFFLINE</span>
                                ) : it.badge ? (
                                    <span className="min-w-[20px] px-1.5 h-5 inline-flex items-center justify-center rounded-full bg-emerald-500 text-white text-[10px] font-bold">{it.badge}</span>
                                ) : null}
                            </button>
                        );
                    })}
                </nav>

                {/* Footer */}
                {onSignOut && (
                    <div className="p-4 border-t border-gray-100">
                        <button
                            onClick={() => { onSignOut(); onClose?.(); }}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                            data-testid="mobile-drawer-signout"
                        >
                            <LogOut className="w-4 h-4" /> Sign out
                        </button>
                    </div>
                )}
            </aside>
        </>
    );
}
